import React, { useState, useEffect, createContext, useContext } from 'react';
import { Frown, Meh, Smile, Camera, Beer, Clock, Calendar, CalendarDays, User, ArrowRight, ArrowLeft, Share2, Wine, AlertTriangle, XCircle } from 'lucide-react';
import { submitSurvey, fetchResultById } from './api/surveyAPI';
import { useNavigate, useParams } from 'react-router-dom';
import ThankYouPage from './ThankYouPage';

// Define types for the application
interface FormData {
  severity: number;
  painLocations: string[];
  consumption: string[];
  duration: string;
  doctor?: string;
}

interface TabItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  component: React.ReactNode;
}

interface ThemeContextType {
  isDarkMode: boolean;
  setIsDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
  hangoverLevel: number;
  setHangoverLevel: React.Dispatch<React.SetStateAction<number>>;
}

interface LocationOption {
  id: string;
  label: string;
}

interface ConsumptionOption {
  id: string;
  label: string;
  icon: React.ReactNode;
}

interface DurationOption {
  id: string;
  label: string;
  icon: React.ReactNode;
}

// Theme context setup
const ThemeContext = createContext<ThemeContextType>({
  isDarkMode: false,
  setIsDarkMode: () => {},
  hangoverLevel: 1,
  setHangoverLevel: () => {}
});

const useTheme = () => useContext(ThemeContext);

const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState<boolean>(true); // Start with dark mode
  const [hangoverLevel, setHangoverLevel] = useState<number>(1);

  useEffect(() => {
    // For severe hangovers (7+), we'll force light mode for the "bright mode" effect
    if (hangoverLevel >= 7) {
      setIsDarkMode(false); // Force light mode for bright effect
    } else {
      setIsDarkMode(true); // Otherwise use dark mode
    }
  }, [hangoverLevel]);

  // Apply the severe hangover container class to enable bright mode when level is 7+
  const containerClass = hangoverLevel >= 7 ? 'severe-hangover-container' : '';

  return (
    <ThemeContext.Provider value={{ isDarkMode, setIsDarkMode, hangoverLevel, setHangoverLevel }}>
      <div className={`${isDarkMode ? 'dark' : 'light'} ${containerClass}`}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

// Welcome Screen Component
interface WelcomeScreenProps {
  onStart: () => void;
  doctor?: string;
}

const WelcomeScreen: React.FC<WelcomeScreenProps> = ({ onStart, doctor = "Matt" }) => {
  return (
    <div className="bg-white dark:bg-gray-900 p-6 rounded-xl shadow-lg max-w-2xl mx-auto">
      <div className="flex items-center mb-8">
        <div className="bg-indigo-600 text-white w-16 h-16 rounded-full flex items-center justify-center mr-4">
          <User size={32} />
        </div>
        <div>
          <h2 className="text-xl font-bold m-0 text-gray-900 dark:text-white">Dr. {doctor}</h2>
          <span className="text-gray-600 dark:text-gray-400 text-sm">Attending Physician</span>
        </div>
      </div>
      
      <div className="text-center my-12">
        <h1 className="text-3xl md:text-4xl font-bold mb-4 text-indigo-600 dark:text-indigo-400">
          How bad does it hurt, champ?
        </h1>
        <p className="text-xl max-w-md mx-auto mb-10 text-gray-700 dark:text-gray-300">
          We heard you had a tough night - let's get you the help you need.
        </p>
        
        <button 
          className="flex items-center justify-center mx-auto bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 rounded-full text-lg font-medium transition-all duration-200 hover:-translate-y-1"
          onClick={onStart}
        >
          <span>Start Survey</span>
          <ArrowRight className="ml-2" size={18} />
        </button>
      </div>
      
      <div className="flex justify-center mt-8">
        <div className="bg-gradient-to-r from-indigo-500 to-red-400 w-48 h-20 rounded-full opacity-20 transform scale-x-150"></div>
      </div>
    </div>
  );
};

// Progress Bar Component
interface ProgressBarProps {
  currentStep: number;
  totalSteps: number;
}

const ProgressBar: React.FC<ProgressBarProps> = ({ currentStep, totalSteps }) => {
  const progress = ((currentStep + 1) / totalSteps) * 100;
  
  return (
    <div className="w-full mb-6">
      <div className="h-2 w-full bg-gray-200 rounded overflow-hidden dark:bg-gray-700">
        <div 
          className="h-full bg-indigo-600 dark:bg-indigo-400" 
          style={{ width: `${progress}%` }}
        ></div>
      </div>
      <div className="text-right text-sm mt-1 text-gray-600 dark:text-gray-400">
        Step {currentStep + 1} of {totalSteps}
      </div>
    </div>
  );
};

// Severity Tab Component
interface SeverityTabProps {
  value: number;
  onChange: (value: number) => void;
}

const SeverityTab: React.FC<SeverityTabProps> = ({ value, onChange }) => {
  const { setHangoverLevel } = useTheme();
  
  const handleChange = (newValue: number) => {
    onChange(newValue);
    setHangoverLevel(newValue);
  };
  
  const getEmoji = (level: number) => {
    if (level <= 3) return <Smile className="text-green-500" size={32} />;
    if (level <= 6) return <Meh className="text-yellow-500" size={32} />;
    return <Frown className="text-red-500" size={32} />;
  };

  return (
    <div className="flex flex-col items-center">
      <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Rate your hangover severity</h3>
      <div className="w-full my-6">
        <input
          type="range"
          min="1"
          max="10"
          value={value}
          onChange={(e) => handleChange(parseInt(e.target.value))}
          className="w-full h-2 bg-gradient-to-r from-green-400 to-red-500 rounded-lg appearance-none cursor-pointer"
        />
        <div className="flex items-center justify-center gap-2 mt-4">
          {getEmoji(value)}
          <span className="text-3xl font-bold text-gray-900 dark:text-white">{value}/10</span>
        </div>
      </div>
      <div className="w-full flex justify-between text-sm text-gray-600 dark:text-gray-400">
        <span>Mild</span>
        <span>Severe</span>
      </div>
      {value >= 7 && (
        <div className="mt-6 p-4 bg-white border border-yellow-400 rounded-lg text-center text-gray-800">
          <p className="font-bold">EXTRA BRIGHT MODE ACTIVATED!</p>
          <p>We've enabled our special BRIGHT mode because Dr. Matt says intense light is "good" for severe hangovers!</p>
          <p className="text-sm mt-2 italic">Stare directly at the screen for best results.</p>
        </div>
      )}
    </div>
  );
};

// Pain Location Tab Component
interface PainLocationTabProps {
  value: string[];
  onChange: (value: string[]) => void;
}

const PainLocationTab: React.FC<PainLocationTabProps> = ({ value, onChange }) => {
  const locations: LocationOption[] = [
    { id: 'head', label: 'Just Head' },
    { id: 'all-over', label: 'All Over' },
    { id: 'spiritual', label: 'Just Spiritually' },
    { id: 'other', label: 'Other' }
  ];
  
  const handleLocationClick = (locId: string) => {
    let newLocations: string[];
    if (value.includes(locId)) {
      newLocations = value.filter(id => id !== locId);
    } else {
      newLocations = [...value, locId];
    }
    onChange(newLocations);
  };

  return (
    <div>
      <h3 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Where does it hurt?</h3>
      
      <div className="grid grid-cols-2 gap-4 mb-6">
        {locations.map((loc) => (
          <button
            key={loc.id}
            className={`p-4 rounded-lg border text-center transition ${
              value.includes(loc.id) 
                ? 'bg-indigo-600 text-white border-indigo-600 dark:bg-indigo-500 dark:border-indigo-500' 
                : 'bg-white text-gray-800 border-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700'
            }`}
            onClick={() => handleLocationClick(loc.id)}
          >
            {loc.label}
          </button>
        ))}
      </div>
      
      <div className="flex justify-center mb-6">
        <div className="relative w-48">
          <svg
            viewBox="0 0 200 300"
            className="w-full"
          >
            <circle cx="100" cy="40" r="30" id="head" 
              className={`stroke-gray-400 dark:stroke-gray-500 ${value.includes('svg:head') ? 'fill-indigo-500' : 'fill-gray-100 dark:fill-gray-800'}`}
              onClick={() => handleLocationClick('svg:head')} />
            <rect x="85" y="70" width="30" height="80" id="torso" 
              className={`stroke-gray-400 dark:stroke-gray-500 ${value.includes('svg:torso') ? 'fill-indigo-500' : 'fill-gray-100 dark:fill-gray-800'}`}
              onClick={() => handleLocationClick('svg:torso')} />
            <rect x="65" y="70" width="20" height="60" id="left-arm" 
              className={`stroke-gray-400 dark:stroke-gray-500 ${value.includes('svg:left-arm') ? 'fill-indigo-500' : 'fill-gray-100 dark:fill-gray-800'}`}
              onClick={() => handleLocationClick('svg:left-arm')} />
            <rect x="115" y="70" width="20" height="60" id="right-arm" 
              className={`stroke-gray-400 dark:stroke-gray-500 ${value.includes('svg:right-arm') ? 'fill-indigo-500' : 'fill-gray-100 dark:fill-gray-800'}`}
              onClick={() => handleLocationClick('svg:right-arm')} />
            <rect x="85" y="150" width="15" height="80" id="left-leg" 
              className={`stroke-gray-400 dark:stroke-gray-500 ${value.includes('svg:left-leg') ? 'fill-indigo-500' : 'fill-gray-100 dark:fill-gray-800'}`}
              onClick={() => handleLocationClick('svg:left-leg')} />
            <rect x="100" y="150" width="15" height="80" id="right-leg" 
              className={`stroke-gray-400 dark:stroke-gray-500 ${value.includes('svg:right-leg') ? 'fill-indigo-500' : 'fill-gray-100 dark:fill-gray-800'}`}
              onClick={() => handleLocationClick('svg:right-leg')} />
          </svg>
          <p className="text-sm text-center text-gray-500 mt-2">Click on body parts to select</p>
        </div>
      </div>
      
      {value.length > 0 && (
        <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg">
          <h4 className="font-bold mb-2 text-gray-900 dark:text-white">Selected areas:</h4>
          <ul className="space-y-1 text-gray-700 dark:text-gray-300">
            {value.map((loc) => {
              if (loc.startsWith('svg:')) {
                return <li key={loc}>{loc.replace('svg:', '').replace('-', ' ')}</li>;
              } else {
                const found = locations.find(l => l.id === loc);
                return <li key={loc}>{found ? found.label : loc}</li>;
              }
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

// Consumption Tab Component
interface ConsumptionTabProps {
  value: string[];
  onChange: (value: string[]) => void;
}

const ConsumptionTab: React.FC<ConsumptionTabProps> = ({ value, onChange }) => {
  const options: ConsumptionOption[] = [
    { id: 'beer', label: 'Beer', icon: <Beer size={32} /> },
    { id: 'wine', label: 'Wine', icon: <Wine size={32} /> },
    { id: 'hard', label: 'Hard Liquor', icon: <Beer size={32} /> },
    { id: 'extra', label: 'Extra Curriculars', icon: <AlertTriangle size={32} /> }
  ];
  
  const toggleOption = (id: string) => {
    let newOptions: string[];
    if (value.includes(id)) {
      newOptions = value.filter(item => item !== id);
    } else {
      newOptions = [...value, id];
    }
    onChange(newOptions);
  };

  return (
    <div>
      <h3 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">What did you consume?</h3>
      <p className="text-gray-600 dark:text-gray-400 mb-6">Select all that apply</p>
      
      <div className="grid grid-cols-2 gap-6 mb-6">
        {options.map((option) => (
          <div
            key={option.id}
            className={`flex flex-col items-center justify-center p-6 rounded-lg border cursor-pointer transition ${
              value.includes(option.id) 
                ? 'bg-indigo-600 text-white border-indigo-600 dark:bg-indigo-500 dark:border-indigo-500' 
                : 'bg-white text-gray-900 border-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700'
            }`}
            onClick={() => toggleOption(option.id)}
          >
            <div className="mb-4">{option.icon}</div>
            <span className="font-medium">{option.label}</span>
          </div>
        ))}
      </div>
      
      {value.length > 0 && (
        <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg text-gray-700 dark:text-gray-300">
          <p>You consumed: {value.map(id => {
            const option = options.find(opt => opt.id === id);
            return option ? option.label : id;
          }).join(', ')}</p>
        </div>
      )}
    </div>
  );
};

// Duration Tab Component
interface DurationTabProps {
  value: string;
  onChange: (value: string) => void;
}

const DurationTab: React.FC<DurationTabProps> = ({ value, onChange }) => {
  const options: DurationOption[] = [
    { id: 'just-today', label: 'Just Today', icon: <Clock size={24} /> },
    { id: 'tomorrow-too', label: 'Tomorrow Too', icon: <Calendar size={24} /> },
    { id: 'days', label: 'Several Days', icon: <CalendarDays size={24} /> }
  ];
  
  const selectOption = (id: string) => {
    onChange(id);
  };

  return (
    <div>
      <h3 className="text-xl font-bold mb-6 text-gray-900 dark:text-white">Will you be in pain for more than today?</h3>
      
      <div className="flex flex-col space-y-4">
        {options.map((option) => (
          <div
            key={option.id}
            className={`flex items-center p-4 rounded-lg border cursor-pointer transition ${
              value === option.id 
                ? 'bg-indigo-600 text-white border-indigo-600 dark:bg-indigo-500 dark:border-indigo-500' 
                : 'bg-white text-gray-900 border-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700'
            }`}
            onClick={() => selectOption(option.id)}
          >
            <div className="mr-4">{option.icon}</div>
            <span className="font-medium">{option.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Survey Container
const HangoverSurvey: React.FC = () => {
  const navigate = useNavigate();
  const { doctor } = useParams<{ doctor: string }>();
  const { isDarkMode, hangoverLevel, setHangoverLevel } = useTheme();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [showWelcome, setShowWelcome] = useState<boolean>(true);
  const [showThankYou, setShowThankYou] = useState<boolean>(false);
  const [resultId, setResultId] = useState<string>('');
  const [formData, setFormData] = useState<FormData>({
    severity: 1,
    painLocations: [],
    consumption: [],
    duration: 'just-today',
    doctor: doctor || 'unknown'
  });

  const tabs: TabItem[] = [
    { 
      id: 'severity', 
      label: 'Severity', 
      icon: <Frown />,
      component: <SeverityTab 
                  value={formData.severity} 
                  onChange={(val) => updateFormData('severity', val)} 
                />
    },
    { 
      id: 'pain-location', 
      label: 'Pain Location', 
      icon: <Camera />,
      component: <PainLocationTab 
                  value={formData.painLocations} 
                  onChange={(val) => updateFormData('painLocations', val)} 
                />
    },
    { 
      id: 'consumption', 
      label: 'Consumption', 
      icon: <Beer />,
      component: <ConsumptionTab 
                  value={formData.consumption} 
                  onChange={(val) => updateFormData('consumption', val)} 
                />
    },
    { 
      id: 'duration', 
      label: 'Duration', 
      icon: <Calendar />,
      component: <DurationTab 
                  value={formData.duration} 
                  onChange={(val) => updateFormData('duration', val)} 
                />
    },
  ];

  const handleStartSurvey = () => {
    setShowWelcome(false);
  };

  const updateFormData = (field: keyof FormData, value: any) => {
    setFormData({
      ...formData,
      [field]: value,
    });
  };

  const handleNext = async () => {
    if (activeTab < tabs.length - 1) {
      setActiveTab(activeTab + 1);
    } else {
      // Submit and show thank you page
      try {
        const id = await submitSurvey(formData);
        setResultId(id);
        setShowThankYou(true);
        // navigate(`/results/${id}`); // Uncomment to navigate directly to results page
      } catch (error) {
        console.error("Error submitting survey:", error);
        // Show error message to user
        alert("Failed to submit survey. Please try again.");
      }
    }
  };

  const handleBack = () => {
    if (activeTab > 0) {
      setActiveTab(activeTab - 1);
    }
  };

  if (showWelcome) {
    return <WelcomeScreen onStart={handleStartSurvey} doctor={doctor} />;
  }

  if (showThankYou) {
    const resultUrl = `${window.location.origin}/results/${resultId || 'abc123'}`;
    return <ThankYouPage resultUrl={resultUrl} />;
  }

  // Apply severe-hangover-container class to make the container bright
  const containerClass = hangoverLevel >= 7 ? 'severe-hangover-container' : '';

  return (
    <div className={`max-w-2xl mx-auto p-6 bg-white dark:bg-gray-900 rounded-xl shadow-lg ${containerClass}`}>
      <header className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
        <h1 className={`text-2xl font-bold text-gray-900 dark:text-white ${hangoverLevel >= 7 ? 'severe-hangover-text' : ''}`}>Hangover Survey</h1>
        <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
          <User size={20} />
          <div className="flex flex-col items-end">
            <span className="font-medium">Dr. {doctor || 'Matt'}</span>
            <span className="text-xs opacity-70">Attending Physician</span>
          </div>
        </div>
      </header>
      
      <ProgressBar currentStep={activeTab} totalSteps={tabs.length} />
      
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{tabs[activeTab].label}</h2>
          <div className="text-indigo-600 dark:text-indigo-400">
            {tabs[activeTab].icon}
          </div>
        </div>
        
        <div className="min-h-64">
          {tabs[activeTab].component}
        </div>
        
        <div className="flex justify-between mt-10">
          {activeTab > 0 ? (
            <button 
              className="flex items-center gap-2 px-4 py-2 border text-gray-700 border-gray-300 dark:text-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
              onClick={handleBack}
            >
              <ArrowLeft size={16} />
              Back
            </button>
          ) : (
            <div></div> // Empty div for spacing
          )}
          <button 
            className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
            onClick={handleNext}
          >
            {activeTab === tabs.length - 1 ? 'Submit' : 'Next'}
            {activeTab !== tabs.length - 1 && <ArrowRight size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};

// Wrap everything in the ThemeProvider
const App: React.FC = () => {
  return (
    <ThemeProvider>
      <div className="min-h-screen bg-gray-100 dark:bg-gray-950 text-gray-900 dark:text-gray-100 p-4 flex items-center justify-center transition-colors duration-300">
        <HangoverSurvey />
      </div>
    </ThemeProvider>
  );
};

export default App;